import { Component, inject, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { IPayment } from "../../models/payment";
import { PaymentService } from "../../services/payments.service";

export const DateFormats = {
  parse: {
    dateInput: ['MM/DD/YYYY']
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'dialog-edit-payment',
  templateUrl: './edit-payment.html',
  providers: [PaymentService, { provide: MAT_DATE_FORMATS, useValue: DateFormats }],
})

export class DialogEditPaymentComponent implements OnInit {
  processing: boolean = false;
  roles: string[] = [];
  today: Date = new Date(Date.now());
  private paymentOptionsLoaded = false;
  public payment: IPayment;
  public entityType: string = '';
  public licenseType: string = '';

  public paymentForm = new FormGroup({
    feeType: new FormControl("", [Validators.required]),
    name: new FormControl(""),
    entityType: new FormControl("", [Validators.required]),
    status: new FormControl("", [Validators.required]),
    paymentMethod: new FormControl("", [Validators.required]),
    sku: new FormControl(""),
    paymentCleared: new FormControl<Date | null>(null),
    amount: new FormControl("", [Validators.required]),
    comment: new FormControl("")
  });

  public paymentService = inject(PaymentService);
  private toastr = inject(ToastrService);
  private dialogRef = inject(MatDialogRef<DialogEditPaymentComponent>)

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {
    this.payment = data.payment || data;
    this.licenseType = data.licenseType || '';
  }

  ngOnInit(): void {
    this.loadPaymentOptionsAndSetForm();
  }

  private loadPaymentOptionsAndSetForm(): void {
    this.paymentService.getPaymentTypes(this.licenseType).subscribe({
      next: (paymentOptions) => this.paymentService.paymentOptions = paymentOptions,
      error: (error) => {
        console.error('Error loading payment options:', error);
        this.toastr.error('Error loading payment options');
        this.setForm(); // Continue with form setup even if options fail to load
      },
      complete: () => {
        this.paymentOptionsLoaded = true;
        this.setForm();
      }
    });
  }

  setForm(): void {
    const matchingOption = this.paymentService.paymentOptions.find(
      option => option.description === this.payment.type
    );

    this.paymentForm.patchValue({
      feeType: matchingOption ? matchingOption.description : '',
      name: matchingOption ? matchingOption.name : '',
      entityType: this.payment.entityType,
      status: this.payment.status,
      sku: this.payment.sku ?? '',
      amount: this.payment.amount,
      comment: this.payment.comment,
      paymentMethod: this.payment.paymentMethod
    });

    if (this.payment.paymentCleared != null && this.payment.paymentCleared.length > 0) {
      this.paymentForm.get('paymentCleared')?.patchValue(new Date(this.payment.paymentCleared));
    }

    if (this.payment.type != null &&
      this.payment.type.length > 0 &&
      (this.payment.amount == null || this.payment.amount.length == 0 || this.payment.amount == "0.00")) {
      this.onChangePaymentType();
    }
  }

  onChangePaymentType() {
    if (this.paymentOptionsLoaded) {
      const feeType = this.paymentForm.get('feeType')?.value ?? '';

      var option = this.paymentService.paymentOptions.find(
        opt => opt.description === feeType
      );

      //const amount = this.paymentService.getPaymentTypeAmount(feeType);
      const amount = option?.fee ?? null;
      const asText = amount != null ? amount.toString() : "";
      this.paymentForm.get('amount')?.patchValue(this.formatMoneyWhileTyping(asText));
      this.paymentForm.get('sku')?.patchValue(option?.sku ?? '');
    }
    let status = this.paymentForm.get('status')?.value;
    if (status == null || status == undefined || status == "") {
      this.paymentForm.get('status')?.patchValue(this.paymentService.paymentPendingStatus);
    }
  }

  savePayment() {
    if (this.paymentForm.valid) {
      this.processing = true;
      let form = this.paymentForm.getRawValue();
      const normalizedRaw = this.sanitizeMoneyInput(form.amount ?? '');
      const amountNumber = Number(normalizedRaw);

      const matchingOption = this.paymentService.paymentOptions.find(
        option => option.description === form.feeType
      );

      this.payment.type = (matchingOption ? matchingOption.name : form.name) ?? '';
      this.payment.sku = form.sku ?? '';
      this.payment.entityType = form.entityType ?? '';
      this.payment.status = form.status ?? '';
      this.payment.amount = amountNumber.toFixed(2);
      this.payment.paymentMethod = form.paymentMethod ?? '';
      this.payment.comment = form.comment ?? '';
      this.payment.paymentCleared = (form.paymentCleared ? form.paymentCleared.toDateString() : null) ?? '';

      if (this.payment.entityId == null || this.payment.entityId == undefined || this.payment.entityId.length == 0) {
        this.toastr.error("Invalid Payment Entity Id");
        this.processing = false;
        return;
      }

      if (this.payment.type == null || this.payment.type == undefined || this.payment.type.length == 0) {
        this.toastr.error("Invalid Payment Type");
        this.processing = false;
        return;
      }

      if (this.payment.amount == null || this.payment.amount == undefined || this.payment.amount.length == 0) {
        this.toastr.error("Invalid Payment amount");
        this.processing = false;
        return;
      }

      if (this.payment.entityType == null || this.payment.entityType == undefined || this.payment.entityType.length == 0) {
        this.payment.entityType = this.payment.entityType;
      }

      if (this.payment.status != null && this.payment.status != undefined && this.payment.status == this.paymentService.paymentClearedStatus) {
        this.payment.paid = true
      }
      else {
        this.payment.paid = false;
      }

      this.paymentService.savePayment(this.payment).subscribe({
        next: response => {
          this.processing = false;
          this.toastr.success("Payment Saved");
          this.dialogRef.close(response);
        },
        error: error => {
          console.error('error: ' + error.statusText);
          this.processing = false;
          this.toastr.error("Unable to save payment");
        }
      });
    }
  }

  cancel() {
    this.dialogRef.close('cancel');
  }

  onAmountKeyDown(event: KeyboardEvent): void {
    const key = event.key;

    // Allow navigation/editing keys
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight'];
    if (allowedKeys.includes(key)) return;

    // Allow copy/paste/select all shortcuts
    if (event.ctrlKey || event.metaKey) return;

    // Allow digits
    if (/^\d$/.test(key)) return;

    // Allow one decimal point
    if (key === '.') {
      const current = String(this.paymentForm.get('amount')?.value ?? '');
      if (!current.includes('.')) return;
    }

    event.preventDefault();
  }

  onAmountPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    const formatted = this.formatMoneyWhileTyping(pasted);

    this.paymentForm.get('amount')?.setValue(formatted);
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    const formatted = this.formatMoneyWhileTyping(input.value);
    if (input.value !== formatted) {
      input.value = formatted;
    }

    // Keep control showing the same formatted value
    this.paymentForm.get('amount')?.setValue(formatted, { emitEvent: false });
  }

  onAmountBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = this.sanitizeMoneyInput(input.value);

    if (!raw) {
      this.paymentForm.get('amount')?.setValue('', { emitEvent: false });
      input.value = '';
      return;
    }

    const n = Number(raw);
    if (!Number.isFinite(n)) return;

    // On blur, finalize to 2 decimals + commas
    const fixed = n.toFixed(2);
    const formatted = this.formatMoneyWhileTyping(fixed);

    input.value = formatted;
    this.paymentForm.get('amount')?.setValue(formatted, { emitEvent: false });
  }

  private sanitizeMoneyInput(value: string): string {
    let v = String(value ?? '').replace(/[^\d.]/g, '');

    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }

    const parts = v.split('.');
    if (parts.length === 2) {
      parts[1] = parts[1].slice(0, 2);
      v = `${parts[0]}.${parts[1]}`;
    }

    return v;
  }

  private formatMoneyWhileTyping(value: string): string {
    const raw = this.sanitizeMoneyInput(value);
    if (!raw) return '';

    const [intPartRaw, decPartRaw] = raw.split('.');
    const intPart = (intPartRaw || '').replace(/^0+(?=\d)/, ''); // trim leading zeros (keep single 0)
    const withCommas = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (decPartRaw == null) return withCommas;
    return `${withCommas}.${decPartRaw}`;
  }

}
